import React, { useState, useEffect } from "react";
import {
  Spin,
  Empty,
  Pagination,
  message as antMessage,
  Card,
  Button,
} from "antd";
import {
  VideoCameraOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  ArrowLeftOutlined,
  MedicineBoxOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { apiGetNotifications } from "../api/dashboard";

const Notifications = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("1"); // 1 = All, 2 = New Patient, 3 = Follow Up
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    fetchNotifications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pagination.current]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await apiGetNotifications({
        page: pagination.current,
        per_page: pagination.pageSize,
        filter_by: parseInt(activeTab),
      });


      // Adjust based on your actual response structure
      const data = response?.data || response || [];
      const total = response?.total || data.length;

      setNotifications(Array.isArray(data) ? data : []);
      setPagination((prev) => ({
        ...prev,
        total: total,
      }));
    } catch (error) {
      console.error("Error fetching notifications:", error);
      antMessage.error("Không thể tải thông báo");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));
  };

  const handleNotificationClick = async (notification) => {
    try {
      const notificationType = notification.type;
      const dataId = notification.dataId || notification.appointment?.id;
      
      switch (notificationType) {
        case 1: // Appointment
          if (dataId) {
            navigate(`/appointments/${dataId}`);
          } else {
            navigate("/appointments");
          }
          break;
          
        case 2: // Side Effect/Health Check
          if (dataId) {
            navigate(`/follow-ups/questionnaire/${dataId}`);
          } else {
            navigate("/follow-ups");
          }
          break;
          
        case 4: // Message/Chat
          navigate("/messages");
          break;
          
        case 6: // Prescription/Shopping
          navigate("/prescriptions");
          break;
          
        case 7: // Patient Profile
          if (dataId) {
            navigate(`/patients/${dataId}`);
          } else {
            navigate("/patients");
          }
          break;
          
        case 8: // Home/Dashboard
          navigate("/");
          break;
          
        case 10: // Video Call - Redirect to Appointment Details
          if (dataId) {
            navigate(`/appointments/${dataId}`);
          } else {
            navigate("/appointments");
          }
          break;
          
        default:
          break;
      }
    } catch (error) {
      console.error("Error handling notification click:", error);
    }
  };

  const getIcon = (notificationType) => {
    switch (notificationType) {
      case 3: // Appointment cancelled
        return <CloseCircleOutlined className="text-red-600 text-xl" />;
      case 5: // New Appointment Booked
        return <CalendarOutlined className="text-green-600 text-xl" />;
      case 6: // Video Call Appointment
        return <VideoCameraOutlined className="text-purple-600 text-xl" />;
      case 8: // Patient Missed Medication
        return <MedicineBoxOutlined className="text-orange-600 text-xl" />;
      case 18: // Patient Joined Video Call
        return <VideoCameraOutlined className="text-blue-600 text-xl" />;
      default:
        return <CalendarOutlined className="text-gray-600 text-xl" />;
    }
  };

  const getNotificationStyle = (notificationType, isSeen) => {
    const baseStyle = !isSeen ? "border-l-4" : "border-l-2";

    switch (notificationType) {
      case 3: // Cancelled
        return `${baseStyle} bg-red-50 border-red-200`;
      case 5: // New Appointment
        return `${baseStyle} bg-green-50 border-green-200`;
      case 6: // Video Call
      case 18: // Patient Joined
        return `${baseStyle} bg-purple-50 border-purple-200`;
      case 8: // Missed Medication
        return `${baseStyle} bg-orange-50 border-orange-200`;
      default:
        return `${baseStyle} bg-gray-50 border-gray-200`;
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";

    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Vừa mới";
    if (diffMins < 60)
      return `${diffMins} minute${diffMins > 1 ? "s" : ""} trước`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} trước`;
    if (diffDays === 1) return "Hôm qua";
    if (diffDays < 7) return `${diffDays} vài ngày trước`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  const getAppointmentDetails = (appointment) => {
    if (!appointment) return null;

    const appointmentDate = new Date(appointment.appointmentTimestamp * 1000);
    const formattedDate = appointmentDate.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    const formattedTime = appointmentDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return (
      <div className="mt-3 p-3 bg-white bg-opacity-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 text-sm">
          <CalendarOutlined className="text-purple-600" />
          <span className="font-medium text-gray-700">
            {formattedDate} lúc {formattedTime}
          </span>
        </div>
        <div className="mt-2 text-xs text-gray-600">
          <span className="font-medium">ID:</span> {appointment.displayId} •
          <span className="font-medium ml-1">Loại:</span>{" "}
          {appointment.appointmentType === 1 ? "Trực tiếp" : "Cuộc gọi video"}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
            >
              <ArrowLeftOutlined />
            </button>
            <span>
              <h1 className="text-3xl font-semibold text-gray-900 mb-2">
                Thông báo
              </h1>
              <p className="text-gray-500">
                Luôn cập nhật thông tin về tình trạng bệnh nhân và lịch hẹn khám sắp tới.
              </p>
            </span>
          </div>
        </div>

        <Card
          title={
            <>
              {/* Tabs */}
              <div className="flex gap-6 ml-0 mt-4 m-2">
                <Button
                  variant="outlined"
                  color={activeTab === "1" && "primary"}
                  size="medium"
                  onClick={() => setActiveTab("1")}
                >
                  Tất cả
                </Button>
                <Button
                  variant="outlined"
                  color={activeTab === "2" && "primary"}
                  size="medium"
                  onClick={() => setActiveTab("2")}
                >
                  Bệnh nhân mới
                </Button>
                <Button
                  variant="outlined"
                  color={activeTab === "3" && "primary"}
                  size="medium"
                  onClick={() => setActiveTab("3")}
                >
                  Theo dõi việc dùng thuốc
                </Button>
              </div>
            </>
          }
        >
          {/* Notifications List */}
          <div className="space-y-4 mb-6">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <Spin size="large" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12">
                <Empty
                  description={
                    <span className="text-gray-500">
                      Không có thông báo nào để hiển thị
                    </span>
                  }
                />
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`bg-white rounded-lg shadow-sm p-6 cursor-pointer hover:shadow-md transition-all ${getNotificationStyle(
                    notification.notificationType,
                    notification.isSeen
                  )} ${!notification.isSeen ? "ring-1 ring-purple-100" : ""}`}
                  style={{ backgroundColor: "#f6f0ff" }}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon - either custom icon from API or default */}
                    <div className="flex-shrink-0 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                      {notification.icon ? (
                        <img
                          src={notification.icon}
                          alt="notification icon"
                          className="w-6 h-6"
                        />
                      ) : (
                        getIcon(notification.notificationType)
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <h3
                          className={`text-lg ${
                            !notification.isSeen
                              ? "font-semibold"
                              : "font-medium"
                          } text-gray-900`}
                        >
                          {notification.title}
                        </h3>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {formatTime(notification.sentAt)}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed mb-2">
                        {notification.message}
                      </p>

                      {/* Show appointment details if available */}
                      {notification.appointment &&
                        getAppointmentDetails(notification.appointment)}
                    </div>

                    {/* Unread indicator */}
                    {!notification.isSeen && (
                      <div className="flex-shrink-0 w-2 h-2 bg-purple-600 rounded-full"></div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {!loading &&
            notifications.length > 0 &&
            pagination.total > pagination.pageSize && (
              <div className="flex justify-center">
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  onChange={handlePageChange}
                  showSizeChanger
                  showTotal={(total) => `Total ${total} notifications`}
                  pageSizeOptions={["10", "20", "50"]}
                />
              </div>
            )}
        </Card>
      </div>
    </div>
  );
};

export default Notifications;
